class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', (event) => {
      if (event.target instanceof Element && event.target.closest('.peta-custom-filter__display')) return;
      if (this.shouldSubmitImmediately(event)) {
        this.onSubmitHandler(event);
        return;
      }
      this.debouncedOnSubmit(event);
    });
    facetForm.addEventListener('click', this.onClickHandler.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    if (FacetFiltersForm.shouldHandleIndexedSearch(searchParams)) {
      FacetFiltersForm.renderIndexedSearch(searchParams, updateURLHash);
      return;
    }

    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const fetchSearchParams = FacetFiltersForm.searchParamsForFetch(searchParams);
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer')?.querySelector('.collection')?.classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${fetchSearchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof window.petaRefreshCollectionGridState === 'function') window.petaRefreshCollectionGridState();
        FacetFiltersForm.scheduleSearchSortRefresh();
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      })
      .catch(() => {
        FacetFiltersForm.finishLoading();
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof window.petaRefreshCollectionGridState === 'function') window.petaRefreshCollectionGridState();
    FacetFiltersForm.scheduleSearchSortRefresh();
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static scheduleSearchSortRefresh() {
    if (typeof window.petaRefreshSearchRecommendedSort !== 'function') return;
    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        if (typeof window.petaRefreshSearchRecommendedSort === 'function') window.petaRefreshSearchRecommendedSort();
      }, 0);
    });
  }

  static renderProductGridContainer(html) {
    const sourceContainer = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer');
    if (!sourceContainer) {
      FacetFiltersForm.finishLoading();
      return;
    }
    document.getElementById('ProductGridContainer').innerHTML = sourceContainer.innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHTML.getElementById('ProductCount') || parsedHTML.getElementById('ProductCountDesktop');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (!sourceCount) {
      FacetFiltersForm.finishLoading();
      return;
    }
    if (container) {
      container.innerHTML = sourceCount.innerHTML;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = sourceCount.innerHTML;
      containerDesktop.classList.remove('loading');
    }
    document.getElementById('ProductGridContainer')?.querySelector('.collection')?.classList.remove('loading');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static finishLoading() {
    document.documentElement.classList.remove('peta-ranked-search-loading');
    const productGridContainer = document.getElementById('ProductGridContainer');
    productGridContainer?.classList.remove('loading');
    productGridContainer?.removeAttribute('aria-busy');
    productGridContainer?.querySelector('.collection')?.classList.remove('loading');
    document.querySelectorAll('#ProductCount, #ProductCountDesktop').forEach((container) => {
      container.classList.remove('loading');
    });
    document
      .querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
      .forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const filterIdentity = (element) => element.id || element.dataset.index || '';
    const isSameFilter = (first, second) => {
      const firstIdentity = filterIdentity(first);
      return Boolean(firstIdentity && firstIdentity === filterIdentity(second));
    };
    const currentFilterFor = (sourceElement) =>
      Array.from(facetDetailsElementsFromDom).find((currentElement) => isSameFilter(currentElement, sourceElement));
    const fetchHasPetaCustomFilters = Array.from(facetDetailsElementsFromFetch).some((sourceElement) =>
      sourceElement.classList.contains('peta-custom-filter')
    );

    // Remove facets that are no longer returned from the server, and clean up duplicate custom facets.
    const seenFilters = new Set();
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      const identity = filterIdentity(currentElement);
      if (identity && seenFilters.has(identity)) {
        currentElement.remove();
        return;
      }
      if (identity) seenFilters.add(identity);
      if (currentElement.classList.contains('peta-custom-filter') && !fetchHasPetaCustomFilters) return;
      if (!Array.from(facetDetailsElementsFromFetch).some((sourceElement) => isSameFilter(currentElement, sourceElement))) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? isSameFilter(element, jsFilter) : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = currentFilterFor(elementToRender);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        currentElement.innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            const previousElement = document.getElementById(previousElementId);
            if (previousElement) {
              previousElement.after(elementToRender);
              return;
            }
          }
        }

        if (elementToRender.parentElement) {
          const insertionTarget = document.querySelector(`#${elementToRender.parentElement.id} .js-filter`);
          if (insertionTarget) insertionTarget.before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    FacetFiltersForm.restorePetaCustomFilters(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static restorePetaCustomFilters(html) {
    const sourceMobileWrapper = html.querySelector('#FacetFiltersFormMobile #FacetsWrapperMobile');
    const targetMobileWrapper = document.querySelector('#FacetFiltersFormMobile #FacetsWrapperMobile');
    if (!sourceMobileWrapper || !targetMobileWrapper) return;

    const sourceCustomFilters = sourceMobileWrapper.querySelectorAll('details.peta-custom-filter');
    const targetCustomFilters = targetMobileWrapper.querySelectorAll('details.peta-custom-filter');
    if (sourceCustomFilters.length && targetCustomFilters.length < sourceCustomFilters.length) {
      targetMobileWrapper.innerHTML = sourceMobileWrapper.innerHTML;
    }

    if (typeof window.petaRenderCollectionFilterPanels === 'function') {
      window.petaRenderCollectionFilterPanels();
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const currentActiveFacetsElement = document.querySelector(selector);
      if (currentActiveFacetsElement) currentActiveFacetsElement.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.peta-mobile-facets-actions', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const sourceElement = html.querySelector(selector);
      const targetElement = document.querySelector(selector);
      if (!sourceElement || !targetElement) return;
      targetElement.innerHTML = sourceElement.innerHTML;
    });

    const mobileFacetForm = document.getElementById('FacetFiltersFormMobile');
    const mobileMenuDrawer = mobileFacetForm ? mobileFacetForm.closest('menu-drawer') : null;
    if (mobileMenuDrawer && typeof mobileMenuDrawer.bindEvents === 'function') mobileMenuDrawer.bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static isCustomSearchSortParams(searchParams) {
    if (window.location.pathname !== '/search' || typeof window.petaRefreshSearchRecommendedSort !== 'function') {
      return false;
    }

    const params = new URLSearchParams(searchParams);
    const customSearchSorts = ['manual', 'created-descending', 'created-ascending', 'price-ascending', 'price-descending'];
    return customSearchSorts.includes(params.get('sort_by')) && Boolean((params.get('q') || '').trim());
  }

  static shouldHandleSearchSortLocally(searchParams, event) {
    if (!FacetFiltersForm.isCustomSearchSortParams(searchParams)) return false;
    const target = event?.target;
    if (!(target instanceof Element)) return false;
    return Boolean(target.matches('select[name="sort_by"]') || target.closest('[data-peta-sort-apply]'));
  }

  static renderSearchSortLocally(searchParams) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    FacetFiltersForm.updateURLHash(searchParams);
    window.petaRefreshSearchRecommendedSort();
  }

  static shouldHandleIndexedSearch(searchParams) {
    if (
      window.location.pathname !== '/search'
      || typeof window.petaRunIndexedSearch !== 'function'
    ) {
      return false;
    }
    const params = new URLSearchParams(searchParams);
    return Boolean((params.get('q') || '').trim());
  }

  static renderIndexedSearch(searchParams, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    window.petaRunIndexedSearch();
  }

  static openMobileSortPanel(wrapper) {
    const sortDetails = wrapper?.querySelector('.peta-mobile-sort-filter');
    const sortSummary = sortDetails?.querySelector('summary');
    if (!sortDetails || !sortSummary) return;
    sortDetails.setAttribute('open', '');
    sortDetails.classList.add('menu-opening');
    sortSummary.setAttribute('aria-expanded', true);
    sortDetails.closest('.has-submenu')?.classList.add('submenu-open');
  }

  static resetMobileSubmenus(wrapper) {
    const main = wrapper?.querySelector('#FacetsWrapperMobile');
    if (!main) return;
    main.scrollLeft = 0;
    main.scrollTop = 0;
    main.classList.remove('submenu-open');
    main.querySelectorAll(':scope > details').forEach((details) => {
      details.classList.remove('menu-opening');
      details.removeAttribute('open');
      details.querySelector('summary')?.setAttribute('aria-expanded', false);
      const submenu = details.querySelector('.mobile-facets__submenu');
      if (submenu) {
        submenu.scrollLeft = 0;
        submenu.scrollTop = 0;
      }
    });
  }

  static resetMobileSubmenuScroll(details) {
    const submenu = details?.querySelector('.mobile-facets__submenu');
    if (!submenu) return;
    const reset = () => {
      const main = details.closest('#FacetsWrapperMobile');
      if (main) {
        main.scrollLeft = 0;
        main.scrollTop = 0;
      }
      submenu.scrollLeft = 0;
      submenu.scrollTop = 0;
    };
    const syncOpenState = () => {
      if (!details.hasAttribute('open')) return;
      details.classList.add('menu-opening');
      details.querySelector('summary')?.setAttribute('aria-expanded', true);
      details.closest('.has-submenu')?.classList.add('submenu-open');
    };
    reset();
    window.requestAnimationFrame(reset);
    window.requestAnimationFrame(syncOpenState);
    window.setTimeout(syncOpenState, 0);
    window.setTimeout(syncOpenState, 120);
    window.setTimeout(reset, 120);
    window.setTimeout(reset, 420);
  }

  static setMobileDrawerMode(wrapper, mode) {
    if (!wrapper) return;
    const isSortMode = mode === 'sort';
    FacetFiltersForm.resetMobileSubmenus(wrapper);
    wrapper.classList.toggle('peta-mobile-sort-mode', isSortMode);
    wrapper.querySelectorAll('.mobile-facets__heading').forEach((heading) => {
      if (!heading.dataset.petaOriginalText) heading.dataset.petaOriginalText = heading.textContent.trim();
      heading.textContent = isSortMode ? '並び替え' : heading.dataset.petaOriginalText;
    });
  }

  static searchParamsForFetch(searchParams) {
    const params = new URLSearchParams(searchParams);
    const customSearchSorts = ['manual', 'created-descending', 'created-ascending', 'price-ascending', 'price-descending'];
    if (window.location.pathname === '/search' && customSearchSorts.includes(params.get('sort_by'))) {
      params.set('sort_by', 'most-relevant');
    }
    return params.toString();
  }

  static preservePetaFilterParams(searchParams) {
    const params = new URLSearchParams(searchParams);
    const currentParams = new URLSearchParams(window.location.search);
    ['filter.p.product_type', 'filter.p.tag', 'peta.category', '_ab', '_fd', '_sc', 'preview_theme_id'].forEach((name) => {
      if (params.has(name)) return;
      currentParams.getAll(name).forEach((value) => {
        if (value) params.append(name, value);
      });
    });
    return params.toString();
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  shouldSubmitImmediately(event) {
    const target = event.target;
    return target instanceof Element && target.matches('[name="sort_by"]');
  }

  onSubmitForm(searchParams, event) {
    searchParams = FacetFiltersForm.preservePetaFilterParams(searchParams);

    if (FacetFiltersForm.shouldHandleIndexedSearch(searchParams)) {
      FacetFiltersForm.renderIndexedSearch(searchParams);
      return;
    }

    if (FacetFiltersForm.shouldHandleSearchSortLocally(searchParams, event)) {
      FacetFiltersForm.renderSearchSortLocally(searchParams);
      return;
    }

    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const target = event.target;
    if (
      target instanceof Element &&
      target.closest('.peta-mobile-sort-filter') &&
      target.matches('input[name="sort_by"]')
    ) {
      const label = target.dataset.sortLabel || target.closest('label')?.textContent?.trim();
      const current = target.closest('.mobile-facets__details')?.querySelector('.peta-mobile-sort-current');
      if (current && label) current.textContent = label;
      return;
    }
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onClickHandler(event) {
    const sortApplyButton = event.target.closest('[data-peta-sort-apply]');
    if (!sortApplyButton) return;
    event.preventDefault();
    const form = sortApplyButton.closest('form');
    if (!form) return;
    this.onSubmitForm(this.createSearchParams(form), event);
    sortApplyButton.closest('.mobile-facets__wrapper')?.querySelector('summary')?.click();
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-peta-mobile-facet-trigger]');
  if (!trigger) return;
  const wrapper = trigger.closest('.mobile-facets__wrapper');
  if (!wrapper) return;
  const mode = trigger.dataset.petaMobileFacetTrigger === 'sort' ? 'sort' : 'filters';
  const mainDetails = wrapper.querySelector('.mobile-facets__disclosure');
  const isDrawerOpen = mainDetails?.hasAttribute('open');
  const applyMode = () => {
    FacetFiltersForm.setMobileDrawerMode(wrapper, mode);
    if (mode === 'sort') FacetFiltersForm.openMobileSortPanel(wrapper);
  };

  if (!isDrawerOpen) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    applyMode();
    mainDetails?.setAttribute('open', '');
    const summary = mainDetails?.querySelector(':scope > summary');
    if (summary && typeof wrapper.openMenuDrawer === 'function') {
      wrapper.openMenuDrawer(summary);
    } else {
      mainDetails?.classList.add('menu-opening');
      summary?.setAttribute('aria-expanded', true);
      document.body.classList.add(`overflow-hidden-${wrapper.dataset.breakpoint}`);
    }
    return;
  }

  if (isDrawerOpen) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    applyMode();
    return;
  }

  window.setTimeout(applyMode, 0);
}, true);

document.addEventListener(
  'click',
  (event) => {
    const summary = event.target.closest(
      '#FacetFiltersFormMobile #FacetsWrapperMobile > details > summary'
    );
    if (!summary) return;
    FacetFiltersForm.resetMobileSubmenuScroll(summary.closest('details'));
  },
  true
);

document.addEventListener(
  'click',
  (event) => {
    const closeButton = event.target.closest('[data-peta-sort-close]');
    if (!closeButton) return;
    const wrapper = closeButton.closest('.mobile-facets__wrapper');
    const summary = wrapper?.querySelector('.mobile-facets__disclosure > summary');
    if (!wrapper || !summary || typeof wrapper.closeMenuDrawer !== 'function') return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    wrapper.closeMenuDrawer(event, summary);
    window.setTimeout(() => {
      FacetFiltersForm.setMobileDrawerMode(wrapper, 'filters');
    }, 240);
  },
  true
);

window.addEventListener('pageshow', () => {
  document.querySelectorAll('.mobile-facets__wrapper').forEach((wrapper) => {
    FacetFiltersForm.setMobileDrawerMode(wrapper, 'filters');
  });
});

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
